import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CHIP = {
  Facebook: "bg-[#1877F2]",
  Instagram: "bg-pink-500",
  X: "bg-slate-800",
  LinkedIn: "bg-[#0A66C2]",
};

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function CalendarGrid({ posts, onDayAdd, onEdit }) {
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });

  const byDay = useMemo(() => {
    const m = {};
    for (const p of posts) {
      if (!p.scheduled_at) continue;
      const d = new Date(p.scheduled_at);
      if (isNaN(d)) continue;
      const k = dayKey(d);
      (m[k] = m[k] || []).push(p);
    }
    Object.values(m).forEach((arr) => arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return m;
  }, [posts]);

  const cells = useMemo(() => {
    const lead = cursor.getDay();
    const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let d = 1; d <= dim; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  const todayK = dayKey(new Date());
  const unscheduled = posts.filter((p) => !p.scheduled_at).length;
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  const goToday = () => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); };
  const chipTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric" }).replace(" ", "").toLowerCase();

  return (
    <div data-testid="calendar-month-grid">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="font-display font-bold text-brand-navy" data-testid="calendar-month-label">{monthLabel}</p>
        <div className="flex items-center gap-1.5">
          <button onClick={goToday} data-testid="calendar-today-btn"
            className="btn-focus h-8 px-3 rounded-full border border-brand-line text-xs font-bold text-brand-navy hover:bg-brand-bg transition-colors">
            Today
          </button>
          <button onClick={() => move(-1)} data-testid="calendar-prev-month"
            className="btn-focus grid place-items-center w-8 h-8 rounded-full border border-brand-line text-brand-navy hover:bg-brand-bg transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => move(1)} data-testid="calendar-next-month"
            className="btn-focus grid place-items-center w-8 h-8 rounded-full border border-brand-line text-brand-navy hover:bg-brand-bg transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-px rounded-xl overflow-hidden border border-brand-line bg-brand-line">
        {WEEKDAYS.map((w) => (
          <div key={w} className="bg-brand-bg px-2 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide text-brand-ink2">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} className="bg-brand-bg/60 min-h-[92px]" />;
          const k = dayKey(d);
          const dayPosts = byDay[k] || [];
          const isToday = k === todayK;
          return (
            <div
              key={k}
              onClick={() => onDayAdd(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0, 0))}
              data-testid={`calendar-day-${k}`}
              className="bg-white min-h-[92px] p-1.5 cursor-pointer hover:bg-brand-bg transition-colors flex flex-col gap-1"
              title="Click to schedule a post on this day"
            >
              <span className={`self-start grid place-items-center w-6 h-6 rounded-full text-xs font-bold ${isToday ? "bg-brand-red text-white" : "text-brand-navy"}`}>
                {d.getDate()}
              </span>
              {dayPosts.slice(0, 3).map((p) => (
                <button
                  key={p.id}
                  onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                  data-testid={`calendar-chip-${p.id}`}
                  className={`btn-focus w-full text-left truncate text-[11px] text-white rounded px-1.5 py-0.5 font-semibold ${CHIP[p.platform] || "bg-slate-500"} ${p.status === "posted" ? "opacity-50" : ""} ${p.status === "failed" ? "ring-2 ring-red-500" : ""}`}
                  title={`${p.platform} · ${p.content}`}
                >
                  {chipTime(p.scheduled_at)} {p.content}
                </button>
              ))}
              {dayPosts.length > 3 && (
                <span className="text-[10px] font-bold text-brand-ink2">+{dayPosts.length - 3} more</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3">
        {Object.entries(CHIP).map(([platform, cls]) => (
          <span key={platform} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-ink2">
            <span className={`w-2.5 h-2.5 rounded-full ${cls}`} /> {platform}
          </span>
        ))}
        {unscheduled > 0 && (
          <span className="text-xs text-brand-ink2 ml-auto" data-testid="calendar-unscheduled-note">
            {unscheduled} post{unscheduled > 1 ? "s" : ""} without a date — visible in List view
          </span>
        )}
      </div>
    </div>
  );
}
